import { useEffect, useState, useRef, useMemo } from "react";
import { imprimirPdf } from "@/lib/download";
import { CargaCamion, EntregaProductos, CargaContador, UnidadesItem, ImprimirEtiquetas } from "@/components/domicilios/CargaEntrega";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Truck, MapPin, Phone, Package, CheckCircle2, Camera, LogOut, RefreshCw,
  ArrowLeft, Play, ChevronUp, ChevronDown, X, Plus, History, Clock, Trash2,
} from "lucide-react";

// -------------------- Estados de entrega --------------------
const ESTADOS_ENTREGA = [
  { key: "entregado", label: "Entregado", color: "bg-blue-600" },
  { key: "no_contesta", label: "No contesta", color: "bg-amber-500" },
  { key: "reprogramado", label: "Reprogramado", color: "bg-teal-500" },
  { key: "falta_producto", label: "Falta producto", color: "bg-orange-500" },
  { key: "danado", label: "Dañado", color: "bg-red-600" },
  { key: "cancelado", label: "Cancelado", color: "bg-zinc-600" },
];
const estadoLabel = (k) => ESTADOS_ENTREGA.find((e) => e.key === k)?.label || (k ? k.replace(/_/g, " ") : "Pendiente");
const estadoColor = (k) => ESTADOS_ENTREGA.find((e) => e.key === k)?.color || "bg-zinc-400";

// -------------------- Utilitario foto --------------------
function readFileAsBase64(file, maxKB = 700) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const maxW = 1200;
        const ratio = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        let q = 0.7;
        let dataUrl = canvas.toDataURL("image/jpeg", q);
        while (dataUrl.length / 1024 > maxKB && q > 0.3) { q -= 0.1; dataUrl = canvas.toDataURL("image/jpeg", q); }
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
export default function AppDomicilios() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [rutaInfo, setRutaInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [entregaOpen, setEntregaOpen] = useState(false);
  const [tab, setTab] = useState("activos"); // activos | historial
  const [puedeMover, setPuedeMover] = useState(false);
  const [carga, setCarga] = useState(null);

  const cargarActivos = async () => {
    try {
      const { data } = await api.get("/domicilios/mis-pedidos");
      setItems(data.items || []);
      setPuedeMover(!!data.puede_mover);
      setCarga(data.carga || null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cargarHistorial = async () => {
    try {
      const { data } = await api.get("/domicilios/mis-historial-hoy");
      setHistorial(data.items || []);
      setRutaInfo(data.ruta || null);
    } catch (e) { /* silencioso */ }
  };
  const load = async () => {
    setLoading(true);
    await Promise.all([cargarActivos(), cargarHistorial()]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const abrirDetalle = async (o) => {
    try {
      const { data } = await api.get(`/domicilios/pedido/${o.id}`);
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Métricas
  const totalPedidos = items.length + historial.length;
  const entregados = historial.filter((h) => h.estado_entrega === "entregado").length;
  const pendientes = items.length;

  // Reordenar (drag & drop simplificado: botones ↑↓)
  const moverItem = async (idx, dir) => {
    if (!puedeMover) return;
    const nueva = [...items];
    const j = idx + dir;
    if (j < 0 || j >= nueva.length) return;
    [nueva[idx], nueva[j]] = [nueva[j], nueva[idx]];
    setItems(nueva);
    try {
      await api.post("/domicilios/orden-ruta", { orden: nueva.map((x) => x.id) });
    } catch (e) { toast.error(formatApiError(e)); cargarActivos(); }
  };

  // Imprimir: primero sincroniza con la ruta actual para que la hoja tenga exactamente los mismos pedidos.
  const imprimirRuta = async () => {
    try {
      const { data } = await api.get("/domicilios/mis-pedidos");
      const frescos = data.items || [];
      if (frescos.map((x) => x.id).join(",") !== items.map((x) => x.id).join(",")) {
        toast.info("La ruta tenía cambios; se actualizó antes de imprimir.");
      }
      setItems(frescos);
      await imprimirPdf(`/domicilios/ruta.pdf?ids=${frescos.map((x) => x.id).join(",")}`);
    } catch (e) { toast.error(e?.response ? formatApiError(e) : String(e.message || e)); cargarActivos(); }
  };

  // Drag & drop touch
  const dragIdx = useRef(null);
  const onDragStart = (i) => (e) => { dragIdx.current = i; e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (i) => async (e) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (!puedeMover || from == null || from === i) return;
    const nueva = [...items];
    const [row] = nueva.splice(from, 1);
    nueva.splice(i, 0, row);
    setItems(nueva);
    dragIdx.current = null;
    try { await api.post("/domicilios/orden-ruta", { orden: nueva.map((x) => x.id) }); } catch (err) { toast.error(formatApiError(err)); cargarActivos(); }
  };

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black text-white shadow-md">
        <div className="px-4 h-14 flex items-center justify-between">
          {selected ? (
            <button onClick={() => setSelected(null)} className="flex items-center gap-1 font-bold text-base" data-testid="back-to-list">
              <ArrowLeft className="w-5 h-5" /> Volver
            </button>
          ) : (
            <div className="flex items-center gap-2 font-black text-lg tracking-tight">
              <Truck className="w-6 h-6" /> DOMICILIOS
            </div>
          )}
          <div className="flex items-center gap-1">
            <button onClick={load} className="p-2" aria-label="Actualizar" data-testid="refresh-btn"><RefreshCw className="w-5 h-5" /></button>
            <button onClick={logout} className="p-2" aria-label="Salir" data-testid="logout-btn"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
        {!selected && (
          <div className="px-4 pb-2 text-xs flex items-center justify-between gap-2">
            <div className="truncate"><span className="opacity-80">Hola,</span> <b>{user?.name || user?.email}</b></div>
            <RutaInicio rutaInfo={rutaInfo} carga={user?.role === "piloto" ? carga : null} onChange={cargarHistorial} />
          </div>
        )}
      </header>

      <main className="px-3 pt-3 pb-28">
        {selected ? (
          <DetalleView
            pedido={selected}
            onEntrega={() => setEntregaOpen(true)}
            onReload={async () => {
              const { data } = await api.get(`/domicilios/pedido/${selected.id}`);
              setSelected(data);
              load();
            }}
          />
        ) : (
          <>
            {/* Contadores */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <ContadorCard label="Total" valor={totalPedidos} color="bg-black" testid="cnt-total" />
              <ContadorCard label="Entregados" valor={entregados} color="bg-emerald-600" testid="cnt-entregados" />
              <ContadorCard label="Pendientes" valor={pendientes} color="bg-amber-500" testid="cnt-pendientes" />
            </div>

            {/* Tabs */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                onClick={() => setTab("activos")}
                className={`h-11 rounded-lg font-black uppercase tracking-widest text-sm border-2 ${tab === "activos" ? "bg-black text-white border-black" : "bg-white text-black border-zinc-300"}`}
                data-testid="tab-activos"
              >
                Activos ({items.length})
              </button>
              <button
                onClick={() => setTab("historial")}
                className={`h-11 rounded-lg font-black uppercase tracking-widest text-sm border-2 ${tab === "historial" ? "bg-black text-white border-black" : "bg-white text-black border-zinc-300"}`}
                data-testid="tab-historial"
              >
                <History className="w-4 h-4 inline mr-1" />Historial ({historial.length})
              </button>
            </div>
            {tab === "activos" && items.length > 0 && <CargaCamion items={items} onChanged={load} />}
            {tab === "activos" && items.length > 0 && (
              <button
                onClick={imprimirRuta}
                className="w-full h-10 mb-3 rounded-lg border-2 border-zinc-300 font-bold text-sm uppercase tracking-widest"
                data-testid="btn-imprimir-ruta"
              >
                Imprimir ruta
              </button>
            )}

            {loading ? (
              <div className="text-center py-16 text-zinc-500 text-base">Cargando…</div>
            ) : tab === "activos" ? (
              items.length === 0 ? (
                <VacioEstado texto="Sin pedidos activos" />
              ) : (
                <div className="space-y-3">
                  {items.map((o, i) => (
                    <PedidoCard
                      key={o.id}
                      pedido={o}
                      index={i}
                      total={items.length}
                      onOpen={() => abrirDetalle(o)}
                      onUp={() => moverItem(i, -1)}
                      onDown={() => moverItem(i, +1)}
                      puedeMover={puedeMover}
                      draggable={puedeMover}
                      onDragStart={onDragStart(i)}
                      onDragOver={onDragOver}
                      onDrop={onDrop(i)}
                    />
                  ))}
                </div>
              )
            ) : (
              historial.length === 0 ? (
                <VacioEstado texto="Sin entregas registradas hoy" />
              ) : (
                <div className="space-y-3">
                  {rutaInfo?.iniciada_at && (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 text-sm text-blue-900 flex items-center gap-2" data-testid="ruta-inicio-info">
                      <Clock className="w-4 h-4" />
                      <span><b>Inicio de ruta:</b> {new Date(rutaInfo.iniciada_at).toLocaleString("es-GT", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}</span>
                    </div>
                  )}
                  {historial.map((o) => (
                    <PedidoCardHistorial key={o.id} pedido={o} onOpen={() => abrirDetalle(o)} />
                  ))}
                </div>
              )
            )}
          </>
        )}
      </main>

      {selected && (
        <EntregaDialog
          open={entregaOpen}
          onClose={() => setEntregaOpen(false)}
          pedido={selected}
          onSaved={async () => {
            setEntregaOpen(false);
            const { data } = await api.get(`/domicilios/pedido/${selected.id}`);
            setSelected(data);
            load();
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// COMPONENTES AUXILIARES
// ============================================================
function ContadorCard({ label, valor, color, testid }) {
  return (
    <div className={`rounded-lg ${color} text-white p-3 text-center`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="text-3xl font-black tabular-nums leading-none mt-1">{valor}</div>
    </div>
  );
}

function VacioEstado({ texto }) {
  return (
    <div className="text-center py-20">
      <Package className="w-14 h-14 text-zinc-300 mx-auto mb-3" />
      <div className="text-zinc-600 font-bold text-lg">{texto}</div>
      <div className="text-xs text-zinc-400 mt-1">Presiona 🔄 para actualizar</div>
    </div>
  );
}

function PedidoCard({ pedido, index, total, onOpen, onUp, onDown, puedeMover, ...dragProps }) {
  const cargaInc = pedido.avance?.total > 0 && pedido.avance.cargados < pedido.avance.total;
  return (
    <div
      className={`${cargaInc ? "bg-amber-50" : "bg-white"} border-2 ${pedido.prioridad_entrega ? "border-red-600" : cargaInc ? "border-amber-400" : "border-zinc-200"} rounded-lg p-4 shadow-sm active:bg-zinc-50 select-none`}
      {...dragProps}
      data-testid={`ped-card-${pedido.numero}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="bg-black text-white text-lg font-black px-2 py-0.5 rounded">{index + 1}</span>
          <span className="text-xl font-black tracking-tight">{pedido.numero}</span>
          <CargaContador pedido={pedido} />
        </div>
        {puedeMover && <div className="flex flex-col gap-1">
          <button onClick={onUp} disabled={index === 0} className="p-1 bg-zinc-100 rounded disabled:opacity-30" aria-label="Subir" data-testid={`up-${pedido.numero}`}><ChevronUp className="w-4 h-4" /></button>
          <button onClick={onDown} disabled={index === total - 1} className="p-1 bg-zinc-100 rounded disabled:opacity-30" aria-label="Bajar" data-testid={`down-${pedido.numero}`}><ChevronDown className="w-4 h-4" /></button>
        </div>}
      </div>
      <button onClick={onOpen} className="w-full text-left space-y-1.5" data-testid={`open-${pedido.numero}`}>
        <div className="text-lg font-bold">{pedido.cliente_nombre}</div>
        <div className="text-base flex items-start gap-1.5"><MapPin className="w-4 h-4 mt-1 shrink-0 text-zinc-500" /><span className="leading-tight">{pedido.direccion || "Sin dirección"}</span></div>
        {(pedido.zona || pedido.departamento) && <div className="text-sm text-zinc-500 pl-5">{[pedido.zona, pedido.departamento].filter(Boolean).join(" · ")}</div>}
        <div className="flex items-center gap-1.5 text-base"><Phone className="w-4 h-4 shrink-0 text-emerald-700" /><span className="font-bold">{pedido.cliente_telefono || "—"}</span></div>
        {pedido.cliente_telefono2 && <div className="flex items-center gap-1.5 text-base"><Phone className="w-4 h-4 shrink-0 text-emerald-700" /><span className="font-bold">{pedido.cliente_telefono2}</span></div>}
        {pedido.notas_ent && <div className="text-sm bg-amber-50 border border-amber-200 rounded p-2 whitespace-pre-wrap" data-testid={`notas-ent-${pedido.numero}`}>{pedido.notas_ent}</div>}
      </button>
      <div className="mt-3 pt-3 border-t border-zinc-200 grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-[10px] uppercase text-zinc-500 font-bold">Total</div>
          <div className="text-sm font-black tabular-nums">{formatMoney(pedido.total)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-zinc-500 font-bold">Envío</div>
          <div className="text-sm font-black tabular-nums text-blue-700">{formatMoney(pedido.envio_precio)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-zinc-500 font-bold">Saldo</div>
          <div className={`text-sm font-black tabular-nums ${pedido.saldo > 0 ? "text-red-600" : "text-emerald-700"}`}>{formatMoney(pedido.saldo)}</div>
        </div>
      </div>
    </div>
  );
}

function PedidoCardHistorial({ pedido, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="w-full text-left bg-white border-2 border-zinc-200 rounded-lg p-3 active:bg-zinc-50"
      data-testid={`hist-card-${pedido.numero}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-lg font-black">{pedido.numero}</span>
        <span className={`text-[10px] font-black text-white px-2 py-1 rounded uppercase tracking-widest ${estadoColor(pedido.estado_entrega)}`}>{estadoLabel(pedido.estado_entrega)}</span>
      </div>
      <div className="text-base font-bold">{pedido.cliente_nombre}</div>
      <div className="text-sm text-zinc-500 truncate">{pedido.direccion}</div>
      <div className="flex justify-between mt-2 text-sm">
        <span className="text-zinc-500">Cobrado: <b className="text-emerald-700">{formatMoney(pedido.pagado_total)}</b></span>
        <span className="text-zinc-500">Saldo: <b className={pedido.saldo > 0 ? "text-red-600" : "text-emerald-700"}>{formatMoney(pedido.saldo)}</b></span>
      </div>
    </button>
  );
}

function EtiquetasAdmin({ pedido }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return null;
  return (
    <div className="mt-3 pt-3 border-t-2 border-zinc-100" data-testid="dom-imprimir-qr">
      <div className="text-sm font-black mb-2">Imprimir etiquetas QR (solo administrador)</div>
      <ImprimirEtiquetas orderId={pedido.id} />
    </div>
  );
}

// GPS: mientras la ruta está iniciada, envía la posición real cada ~30 s o cada 50 m.
function useRastreoGPS(activo) {
  useEffect(() => {
    if (!activo || !navigator.geolocation) return undefined;
    let ult = { at: 0, lat: 0, lng: 0 };
    let parar = false;
    const id = navigator.geolocation.watchPosition((pos) => {
      const { latitude: lat, longitude: lng, accuracy } = pos.coords;
      const now = Date.now();
      const mov = Math.abs(lat - ult.lat) + Math.abs(lng - ult.lng) > 0.00045;
      if (parar || (now - ult.at < 30000 && !mov) || now - ult.at < 8000) return;
      ult = { at: now, lat, lng };
      api.post("/domicilios/ruta/ping", { lat, lng, accuracy }).then(({ data }) => { if (data?.finalizada) parar = true; }).catch(() => {});
    }, () => {}, { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [activo]);
}

function RutaInicio({ rutaInfo, carga, onChange }) {
  const [busy, setBusy] = useState(false);
  const iniciada = !!rutaInfo?.iniciada_at;
  const bloqueada = !!carga && !carga.completo && !carga.autorizado;
  useRastreoGPS(iniciada && !rutaInfo?.finalizada_at);
  const getGeo = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: 0, lng: 0 });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve({ lat: 0, lng: 0 }),
      { enableHighAccuracy: true, timeout: 5000 }
    );
  });
  const iniciar = async () => {
    setBusy(true);
    try {
      const g = await getGeo();
      await api.post("/domicilios/ruta/iniciar", g);
      toast.success("Ruta iniciada");
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  if (iniciada) {
    return <span className={`text-[10px] font-black uppercase tracking-widest ${rutaInfo?.finalizada_at ? "bg-zinc-700" : "bg-emerald-600"} text-white px-2 py-1 rounded shrink-0`} data-testid="ruta-iniciada">{rutaInfo?.finalizada_at ? (rutaInfo.cierre_automatico ? "Ruta cerrada 8 p.m." : "Ruta finalizada") : "Ruta iniciada · GPS"}</span>;
  }
  if (bloqueada) {
    return (
      <button onClick={() => toast.error(`Iniciar bloqueado: cargados ${carga.cargados}/${carga.total} · resumen ${carga.marcados}/${carga.total}. Cargue todo y complete el resumen, o pida autorización al administrador.`)}
        className="flex items-center gap-1 bg-zinc-600 text-white text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded shrink-0 opacity-80" data-testid="ruta-iniciar-bloqueado">
        <Play className="w-3 h-3" /> Iniciar · {carga.cargados}/{carga.total}
      </button>
    );
  }
  return (
    <button onClick={iniciar} disabled={busy} className="flex items-center gap-1 bg-[#16A34A] text-white text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded shrink-0" data-testid="ruta-iniciar">
      <Play className="w-3 h-3" /> Iniciar ruta
    </button>
  );
}

// ============================================================
// VISTA DETALLE
// ============================================================
function DetalleView({ pedido, onEntrega, onReload }) {
  const estadoTerminal = ["entregado", "no_contesta", "reprogramado", "falta_producto", "danado", "cancelado"].includes(pedido.estado_entrega);
  return (
    <div className="space-y-3">
      {/* Cabecera pedido */}
      <div className="bg-white border-2 border-black rounded-lg p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Pedido</div>
            <div className="font-black text-2xl leading-tight">{pedido.numero}</div>
          </div>
          <span className={`text-[10px] font-black text-white px-3 py-1.5 rounded uppercase tracking-widest ${estadoColor(pedido.estado_entrega)}`}>{estadoLabel(pedido.estado_entrega)}</span>
        </div>
        <div className="text-lg font-bold mb-2">{pedido.cliente_nombre}</div>
        <div className="space-y-1.5 text-base">
          <div className="flex items-start gap-2"><MapPin className="w-5 h-5 shrink-0 mt-0.5 text-zinc-500" /><span className="font-medium">{pedido.direccion || "—"}{pedido.zona ? ` · ${pedido.zona}` : ""}{pedido.departamento ? ` · ${pedido.departamento}` : ""}</span></div>
          {pedido.referencia && <div className="pl-7 italic text-zinc-500 text-sm">Ref: {pedido.referencia}</div>}
          {pedido.cliente_telefono && (
            <a href={`tel:${pedido.cliente_telefono}`} className="flex items-center gap-2 bg-[#25D366] text-white px-4 py-4 rounded-xl font-black text-xl w-full justify-center" data-testid="call-tel1">
              <Phone className="w-7 h-7" /> Llamar {pedido.cliente_telefono}
            </a>
          )}
          {pedido.cliente_telefono2 && (
            <a href={`tel:${pedido.cliente_telefono2}`} className="flex items-center gap-2 bg-[#25D366] text-white px-4 py-4 rounded-xl font-black text-xl w-full justify-center" data-testid="call-tel2">
              <Phone className="w-7 h-7" /> Llamar {pedido.cliente_telefono2}
            </a>
          )}
          {pedido.direccion && (
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((pedido.direccion || "") + " " + (pedido.zona || "") + " " + (pedido.departamento || ""))}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-black text-white px-4 py-4 rounded-xl font-black text-xl w-full justify-center" data-testid="open-map">
              <MapPin className="w-7 h-7" /> Ver en Google Maps
            </a>
          )}
        </div>
        {pedido.notas && (
          <div className="mt-3 p-3 bg-amber-50 border-2 border-amber-200 rounded text-sm">
            <b>Notas:</b> {pedido.notas}
          </div>
        )}
        {pedido.notas_ent && (
          <div className="mt-3 p-3 bg-amber-50 border-2 border-amber-200 rounded text-sm whitespace-pre-wrap" data-testid="detalle-notas-ent">
            <b>Notas de entrega:</b> {pedido.notas_ent}
          </div>
        )}
      </div>

      {/* Productos */}
      <div className="bg-white border-2 border-zinc-200 rounded-lg p-4">
        <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">Productos ({(pedido.items || []).length})</div>
        <div className="space-y-2">
          {(pedido.items || []).map((it, i) => {
            const us = (pedido.unidades || []).filter((u) => u.item === i + 1);
            const listo = us.length > 0 && us.every((u) => u.entregado);
            return (
            <div key={i} className={`border-b border-zinc-100 pb-2 last:border-0 last:pb-0 ${listo ? "bg-emerald-50 -mx-2 px-2 rounded" : ""}`} data-testid={`det-item-${i + 1}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className={`text-base font-semibold ${listo ? "text-emerald-800" : ""}`}>
                    {listo && <CheckCircle2 className="w-4 h-4 inline mr-1 text-emerald-600" />}
                    <span className="text-black font-black mr-1">{it.cantidad}×</span>
                    {it.product_name}
                  </div>
                  {(it.color || it.tamano || it.estilo || it.tela) && (
                    <div className="text-sm text-zinc-500">{[it.color, it.tamano, it.estilo, it.tela].filter(Boolean).join(" · ")}</div>
                  )}
                  <UnidadesItem pedido={pedido} idx={i} />
                </div>
                <div className="text-right text-base font-semibold tabular-nums">{formatMoney((it.subtotal ?? (it.precio_unit * it.cantidad)) || 0)}</div>
              </div>
            </div>
            );
          })}
        </div>
        <EntregaProductos pedido={pedido} onChanged={onReload} onCompleto={onEntrega} />
        <EtiquetasAdmin pedido={pedido} />
      </div>

      {/* Totales — 5 valores según Ronda 16:
          Precio | Precio de envío | Total | Anticipado | Saldo.
          "Anticipado" = anticipos y pagos previos del pedido (NO lo cobrado
          por el repartidor). Saldo = Total − Anticipado − cobrado por
          repartidor; se actualiza automáticamente al registrar cada pago. */}
      <div className="bg-black text-white rounded-lg p-4" data-testid="detalle-totales">
        <div className="grid grid-cols-3 gap-x-2 gap-y-3 text-center">
          <div className="min-w-0">
            <div className="text-[10px] uppercase text-zinc-400 tracking-wider">Precio</div>
            <div className="font-black text-sm sm:text-base tabular-nums break-words" data-testid="pedido-precio">{formatMoney(pedido.subtotal || (pedido.total - (pedido.envio_precio || 0)))}</div>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase text-zinc-400 tracking-wider">Envío</div>
            <div className="font-black text-sm sm:text-base tabular-nums text-blue-300 break-words" data-testid="pedido-envio">{formatMoney(pedido.envio_precio)}</div>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase text-zinc-400 tracking-wider">Total</div>
            <div className="font-black text-sm sm:text-base tabular-nums break-words" data-testid="pedido-total">{formatMoney(pedido.total)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-center mt-3 pt-3 border-t border-zinc-700">
          <div className="min-w-0">
            <div className="text-[10px] uppercase text-zinc-400 tracking-wider">Anticipado</div>
            <div className="font-black text-lg tabular-nums text-emerald-400 break-words" data-testid="pedido-anticipado">{formatMoney(pedido.anticipo || 0)}</div>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase text-[#FFB800] tracking-wider">Saldo</div>
            <div className={`font-black text-xl tabular-nums break-words ${pedido.saldo > 0 ? "text-[#FFB800]" : "text-emerald-400"}`} data-testid="pedido-saldo">{formatMoney(pedido.saldo)}</div>
          </div>
        </div>
      </div>

      {/* Pagos ya registrados */}
      {(pedido.pagos_domicilio || []).length > 0 && (
        <div className="bg-white border-2 border-zinc-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">Pagos registrados ({(pedido.pagos_domicilio || []).length})</div>
          <div className="space-y-2">
            {(pedido.pagos_domicilio || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-base border-b border-zinc-100 pb-2 last:border-0 last:pb-0">
                <div>
                  <div className="font-bold">{p.metodo_pago}{p.banco ? ` · ${p.banco}` : ""}</div>
                  <div className="text-xs text-zinc-500">{(p.registrado_at || "").slice(0, 16).replace("T", " ")}</div>
                  {p.referencia && <div className="text-xs text-zinc-500">Ref: {p.referencia}</div>}
                  {p.comprobante_foto && <a href={p.comprobante_foto} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline">Ver comprobante</a>}
                </div>
                <div className="text-right tabular-nums font-black text-emerald-700 text-lg">{formatMoney(p.monto)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Foto entrega si existe */}
      {pedido.entrega_foto && (
        <div className="bg-white border-2 border-zinc-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">Foto de entrega</div>
          <img src={pedido.entrega_foto} alt="Entrega" className="w-full rounded-lg" />
          {pedido.entrega_comentario && <div className="text-sm italic text-zinc-600 mt-2">"{pedido.entrega_comentario}"</div>}
        </div>
      )}

      {/* CTA fijo abajo */}
      {!estadoTerminal && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-zinc-200 p-3 shadow-2xl">
          <Button
            onClick={onEntrega}
            className="w-full h-20 bg-black hover:bg-zinc-800 text-white font-black uppercase tracking-widest text-xl rounded-xl"
            data-testid="btn-registrar-entrega"
          >
            <CheckCircle2 className="w-7 h-7 mr-2" /> Registrar pago
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// DIÁLOGO: REGISTRAR ENTREGA (con pagos múltiples)
// ============================================================
function EntregaDialog({ open, onClose, pedido, onSaved }) {
  const { user } = useAuth();
  const esAdmin = ["admin", "gerente_admin"].includes(user?.role);
  const faltan = (pedido.avance?.total || 0) - (pedido.avance?.entregados || 0);
  const [autorizar, setAutorizar] = useState(false);
  const [motivoAut, setMotivoAut] = useState("");
  const [estado, setEstado] = useState("entregado");
  const [comentario, setComentario] = useState("");
  const [fechaReprog, setFechaReprog] = useState("");
  const [pagos, setPagos] = useState([]); // pagos nuevos a agregar en esta sesión
  const [saving, setSaving] = useState(false);
  const [bancos, setBancos] = useState([]);

  useEffect(() => {
    if (open) {
      setEstado("entregado");
      setAutorizar(false);
      setMotivoAut("");
      setComentario("");
      setFechaReprog("");
      setPagos([]);
      api.get("/bancos").then(({ data }) => setBancos(data || [])).catch(() => setBancos([]));
    }
  }, [open]);

  const totalPagosNuevos = useMemo(() => pagos.reduce((s, p) => s + Number(p.monto || 0), 0), [pagos]);
  const saldoActual = Math.max(0, (pedido.saldo || 0) - totalPagosNuevos);

  const addPago = () => setPagos((prev) => [...prev, {
    metodo_pago: "Efectivo", monto: "", banco: "", referencia: "", numero_cuenta: "", comprobante_foto: "",
  }]);
  const updPago = (idx, patch) => setPagos((prev) => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  const delPago = (idx) => setPagos((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    if (estado === "reprogramado" && !fechaReprog) {
      toast.error("Selecciona la fecha de reprogramación");
      return;
    }
    if (estado === "entregado" && faltan > 0 && !(esAdmin && autorizar && motivoAut.trim().length >= 3)) {
      toast.error(esAdmin ? `Faltan ${faltan} producto(s) por escanear. Marque la autorización y escriba el motivo.` : `Faltan ${faltan} producto(s) por escanear. Escanéelos antes de marcar Entregado.`);
      return;
    }
    // La entrega se confirma con el escaneo completo: ya no se pide fotografía.
    // Validar pagos
    for (const [i, p] of pagos.entries()) {
      if (!p.monto || Number(p.monto) <= 0) { toast.error(`Pago ${i + 1}: monto inválido`); return; }
      if (p.metodo_pago === "Banco" && !p.banco) { toast.error(`Pago ${i + 1}: elige un banco`); return; }
      if (p.metodo_pago === "Banco" && !p.comprobante_foto) { toast.error(`Pago ${i + 1}: la foto del comprobante es obligatoria`); return; }
    }
    setSaving(true);
    try {
      // 1) Guardar pagos primero (si hay)
      for (const p of pagos) {
        await api.post(`/domicilios/pedido/${pedido.id}/pago`, {
          metodo_pago: p.metodo_pago,
          monto: Number(p.monto),
          banco: p.banco || "",
          referencia: p.referencia || "",
          numero_cuenta: p.numero_cuenta || "",
          comprobante_foto: p.comprobante_foto || "",
        });
      }
      // 2) Registrar entrega
      await api.post(`/domicilios/pedido/${pedido.id}/entrega`, {
        estado,
        foto: "",
        comentario: comentario || "",
        fecha_reprogramada: fechaReprog || "",
        autorizar_sin_escaneo: estado === "entregado" && faltan > 0 && autorizar,
        motivo_autorizacion: motivoAut,
      });
      toast.success("Pago registrado");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-4">
        <DialogHeader>
          <DialogTitle className="text-xl font-black">Registrar pago — {pedido.numero}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Estado */}
          <div>
            <label className="text-xs font-black uppercase tracking-widest text-zinc-600 block mb-2">Estado de entrega</label>
            <div className="grid grid-cols-2 gap-2">
              {ESTADOS_ENTREGA.map((e) => (
                <button
                  key={e.key}
                  onClick={() => setEstado(e.key)}
                  className={`p-3 border-2 rounded-lg text-sm font-black uppercase transition ${estado === e.key ? `${e.color} text-white border-transparent` : "bg-white text-black border-zinc-300"}`}
                  data-testid={`estado-${e.key}`}
                >
                  {e.label}
                </button>
              ))}
            </div>
          </div>

          {estado === "reprogramado" && (
            <div>
              <label className="text-xs font-black uppercase tracking-widest text-zinc-600 block mb-1">Nueva fecha</label>
              <Input type="date" value={fechaReprog} onChange={(e) => setFechaReprog(e.target.value)} className="h-11" data-testid="fecha-reprog" />
            </div>
          )}

          {/* Pagos múltiples */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-black uppercase tracking-widest text-zinc-600">Pagos ({pagos.length})</label>
              <div className="text-sm">Saldo: <b className={saldoActual > 0 ? "text-red-600" : "text-emerald-700"}>{formatMoney(saldoActual)}</b></div>
            </div>
            {pagos.map((p, i) => (
              <PagoRow key={i} idx={i} pago={p} bancos={bancos} onChange={(patch) => updPago(i, patch)} onDelete={() => delPago(i)} />
            ))}
            <Button variant="outline" onClick={addPago} className="w-full h-11 mt-2 border-2 border-dashed" data-testid="add-pago">
              <Plus className="w-4 h-4 mr-1" /> Agregar otro pago
            </Button>
          </div>

          {/* Comentarios */}
          {estado === "entregado" && faltan > 0 && (
            <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3 text-sm space-y-2" data-testid="entrega-faltan">
              <b>Faltan {faltan} producto(s) por escanear.</b>
              {esAdmin ? (
                <>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={autorizar} onChange={(e) => setAutorizar(e.target.checked)} data-testid="entrega-autorizar" /> Autorizar entrega sin escanear todo</label>
                  {autorizar && <Input value={motivoAut} onChange={(e) => setMotivoAut(e.target.value)} placeholder="Motivo de la autorización" data-testid="entrega-autorizar-motivo" />}
                </>
              ) : <div>Escanee los productos en «Escanear producto» antes de marcar Entregado.</div>}
            </div>
          )}
          <div>
            <label className="text-xs font-black uppercase tracking-widest text-zinc-600 block mb-1">Comentarios</label>
            <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={3} placeholder="Observaciones, quién recibió, etc." data-testid="entrega-comentario" />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving} className="h-11">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="h-11 bg-black text-white font-black" data-testid="btn-guardar-entrega">
            {saving ? "Guardando…" : "Guardar pago"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PagoRow({ idx, pago, bancos, onChange, onDelete }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const onCompFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { onChange({ comprobante_foto: await readFileAsBase64(file, 500) }); } catch { toast.error("No se pudo leer la foto"); }
  };
  return (
    <div className="border-2 border-zinc-200 rounded-lg p-3 mt-2 space-y-2" data-testid={`pago-row-${idx}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-black uppercase tracking-widest">Pago {idx + 1}</div>
        <button onClick={onDelete} className="p-1 text-red-600" aria-label="Eliminar" data-testid={`del-pago-${idx}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => onChange({ metodo_pago: "Efectivo", banco: "" })}
          className={`p-2 border-2 rounded font-bold text-sm ${pago.metodo_pago === "Efectivo" ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-zinc-300 text-zinc-500"}`}
          data-testid={`pago-efectivo-${idx}`}
        >Efectivo</button>
        <button
          onClick={() => onChange({ metodo_pago: "Banco" })}
          className={`p-2 border-2 rounded font-bold text-sm ${pago.metodo_pago === "Banco" ? "border-blue-700 bg-blue-50 text-blue-800" : "border-zinc-300 text-zinc-500"}`}
          data-testid={`pago-banco-${idx}`}
        >Banco</button>
      </div>
      <div>
        <label className="text-[10px] font-black uppercase text-zinc-500">Monto</label>
        <Input type="number" min={0} step="0.01" value={pago.monto} onChange={(e) => onChange({ monto: e.target.value })} placeholder="0.00" className="h-11 text-xl text-right font-black tabular-nums" data-testid={`pago-monto-${idx}`} />
      </div>
      {pago.metodo_pago === "Banco" && (
        <>
          <div>
            <label className="text-[10px] font-black uppercase text-zinc-500">Banco</label>
            <select
              value={pago.banco}
              onChange={(e) => onChange({ banco: e.target.value })}
              className="w-full h-11 border-2 border-zinc-200 rounded px-2 text-base"
              data-testid={`pago-banco-select-${idx}`}
            >
              <option value="">— Selecciona banco —</option>
              {bancos.map((b) => <option key={b.id} value={b.nombre}>{b.nombre}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-black uppercase text-zinc-500">Referencia</label>
              <Input value={pago.referencia} onChange={(e) => onChange({ referencia: e.target.value })} className="h-10" placeholder="N° ref." data-testid={`pago-ref-${idx}`} />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-zinc-500">N° cuenta</label>
              <Input value={pago.numero_cuenta} onChange={(e) => onChange({ numero_cuenta: e.target.value })} className="h-10" placeholder="Opcional" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase text-red-700 block mb-1">Comprobante (obligatorio)</label>
            <input ref={fileRef} type="file" accept="image/*" onChange={onCompFoto} className="hidden" />
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onCompFoto} className="hidden" />
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => cameraRef.current?.click()} className={`h-10 text-sm ${pago.comprobante_foto ? "" : "border-red-400 border-2"}`} data-testid={`pago-comp-camera-${idx}`}>
                <Camera className="w-4 h-4 mr-1" /> Tomar foto
              </Button>
              <Button variant="outline" onClick={() => fileRef.current?.click()} className={`h-10 text-sm ${pago.comprobante_foto ? "" : "border-red-400 border-2"}`} data-testid={`pago-comp-upload-${idx}`}>
                <Plus className="w-4 h-4 mr-1" /> Subir foto
              </Button>
            </div>
            {pago.comprobante_foto && <div className="text-[10px] text-emerald-700 mt-1">✓ Foto cargada</div>}
            {pago.comprobante_foto && <img src={pago.comprobante_foto} alt="comp" className="mt-2 w-full max-h-32 object-contain border rounded" />}
          </div>
        </>
      )}
    </div>
  );
}
